import React, { FC, useState, useEffect } from 'react'
import 'react-phone-number-input/style.css'
import { Box, Typography } from '@mui/material'
import {
  BaseLayout,
  Module,
  ModuleHeader,
  ModuleBody,
  ButtonContainer,
  CustomButton,
  Loader,
} from '../../components'
import useStyles from './enroll-styles'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import { getAuthClient, routes } from 'config'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'

import { processErrorMsg, storage, CountryChangeMode } from 'utils'
const Enroll: FC = () => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { oktaAuth } = useOktaAuth()
  const [transaction, setTransaction] = useState<any | {}>({})
  const [showLoader, setShowLoader] = useState(false)
  const [serviceError, setServiceError] = useState('')
  const [isNumberValid, setIsNumberValid] = useState<boolean>(false)
  const [phoneNumber, setPhoneNumber] = useState<any>('')
  const [country, setCountry] = useState<any>('US')
  const { state } = useLocation()
  const [phoneInputFocus, setPhoneInputFocus] = useState(false)
  const [countryChangeMode, setCountryChangeMode] = useState<CountryChangeMode>(
    CountryChangeMode.AUTOMATICALLY,
  )

  // clears the input field if user selects another flag
  useEffect(() => {
    if (country && countryChangeMode === CountryChangeMode.MANUALLY) {
      setPhoneNumber('')
    }
    if (state?.poppedFromResend && state?.mobileNumber) {
      // sent back after tapping on resend code
      setPhoneNumber(state.mobileNumber)
    }
  }, [country, state])

  useEffect(() => {
    if (phoneNumber && phoneNumber === '+1') {
      setCountry('US')
    } // plugin doesnot sets the country on its own if +1 is enetered, that's why setting US by default
  }, [phoneNumber])

  useEffect(() => {
    let authClientDetails = getAuthClient()

    if (authClientDetails.tx.exists()) {
      setShowLoader(true)
      if (state?.poppedFromResend && state?.mobileNumber) {
        // fill in the saved number as user is sent back from resend
        setPhoneNumber(state.mobileNumber)
      }
      authClientDetails.tx
        .resume()
        .then((response: any) => {
          storage.setUser(response?.user?.profile?.login)
          setTransaction(response)
          setShowLoader(false)
        })
        .catch((e: any) => processError(e, 'MFA', 'Resuming transaction'))
    } else {
      navigate(routes.login)
    }
  }, [navigate, state])

  const validatePhoneNumberInternally = (
    country: string,
    phoneNumber: string,
  ) => {
    if (country === 'US' || country === 'CA') {
      phoneNumber?.length === 12
        ? setIsNumberValid(true)
        : setIsNumberValid(false)
    } else if (country === 'IN') {
      phoneNumber?.length === 13
        ? setIsNumberValid(true)
        : setIsNumberValid(false)
    } else if (phoneNumber) setIsNumberValid(isValidPhoneNumber(phoneNumber))
    else {
      setIsNumberValid(false)
    }
  }

  useEffect(() => {
    validatePhoneNumberInternally(country, phoneNumber)
  }, [phoneNumber, country])

  const processSuccess = (response: any) => {
    setShowLoader(false)
    setTransaction(response)
    navigate(routes.mfa)
  }

  const processError = (
    e: any,
    module: string,
    moduleDescription: string = '',
  ) => {
    setShowLoader(false)
    setServiceError(processErrorMsg(e, module, moduleDescription))
  }

  if (transaction.sessionToken) {
    oktaAuth.signInWithRedirect({ sessionToken: transaction.sessionToken })
    return null
  }

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    type: string,
  ): void => {
    e.preventDefault()

    if (type === 'continue') {
      // this will be used in case of MFA_ENROLL_ACTIVATE stat.
      localStorage.setItem('mobileNumber', phoneNumber)
      setServiceError('')
      setShowLoader(true)
      console.log('transactions', transaction)
      const factor = transaction.factors.find(
        (value: any) => value.factorType === 'sms',
      )
      factor
        .enroll({
          profile: {
            phoneNumber: phoneNumber,
            updatePhone: true,
          },
        })
        .then(processSuccess)
        .catch(processError)
    } else {
      navigate(routes.login)
    }
  }

  const renderContent = () => {
    if (!showLoader) {
      return (
        <>
          <ModuleHeader
            title="Two-Factor Authentication"
            isNewLayout
            className={classes.title}
          >
            <Typography variant="body1" className={classes.description}>
              Bright has added an extra layer of security to ensure only you can
              access the information you save in the MLS.
              <br />
              <br />
              To complete this process, please add your mobile number and click
              continue.
            </Typography>
            <br />
          </ModuleHeader>

          <ModuleBody isNewLayout>
            <Box className={classes.numberInput}>
              <Typography className={classes.learnMoreLink} variant="body2">
                Mobile Number
              </Typography>
              <PhoneInput
                placeholder="Enter phone number"
                initialValueFormat="national"
                defaultCountry={country}
                value={phoneNumber}
                onChange={(value) => {
                  setPhoneInputFocus(true)
                  setPhoneNumber(value)
                }}
                onCountryChange={(value) => {
                  setCountryChangeMode(
                    phoneInputFocus
                      ? CountryChangeMode.AUTOMATICALLY
                      : CountryChangeMode.MANUALLY,
                  )
                  setCountry(value)
                }}
                onBlur={() => {
                  setPhoneInputFocus(false)
                }}
              />
            </Box>
            <br />
            <ButtonContainer
              primary={
                <CustomButton
                  onClick={(e) => handleClick(e, 'continue')}
                  disabled={!isNumberValid}
                  type="submit"
                >
                  Continue
                </CustomButton>
              }
              secondary={
                <CustomButton
                  onClick={(e) => handleClick(e, 'back')}
                  type="submit"
                >
                  Back
                </CustomButton>
              }
            />
          </ModuleBody>
        </>
      )
    }
  }

  return (
    <BaseLayout maxWidth="38em">
      <Loader visible={showLoader} />
      <Module
        isModuleInitialized={Boolean(transaction.status)}
        serviceError={serviceError}
      >
        {renderContent()}
      </Module>
    </BaseLayout>
  )
}

export default Enroll
